import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Navigation.css';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { ShoppingBagIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import AccountSettings from './AccountSettings';
import { requestHomeSection, scrollToSection } from '../utils/siteNavigation';
import { useSearch, SearchCategory } from '../contexts/SearchContext';
import { SEARCH_CATEGORY_OPTIONS } from '../constants/productCategories';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDropdownAnimating, setIsDropdownAnimating] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState('profile');
  const { isAuthenticated, openAuthModal } = useAuth();
  const { cartCount, openCart } = useCart();
  const {
    draftQuery,
    draftCategory,
    setDraftQuery,
    setDraftCategory,
    submitSearch,
  } = useSearch();

  const selectedCategoryLabel =
    SEARCH_CATEGORY_OPTIONS.find((option) => option.id === draftCategory)?.label || 'All';

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleOpenAccountSettings = (event: Event) => {
      const tab = (event as CustomEvent<{ tab?: string }>).detail?.tab || 'profile';
      setSettingsInitialTab(tab);
      setIsSettingsOpen(true);
    };

    window.addEventListener('worldasia:open-account-settings', handleOpenAccountSettings);
    return () => window.removeEventListener('worldasia:open-account-settings', handleOpenAccountSettings);
  }, []);

  // Handle dropdown animation
  useEffect(() => {
    if (isDropdownOpen) {
      setIsDropdownAnimating(true);
      setIsDropdownVisible(true);
    } else {
      setIsDropdownAnimating(false);
      // Delay hiding to allow for exit animation
      const timer = setTimeout(() => {
        setIsDropdownVisible(false);
      }, 300); // Match CSS transition duration
      return () => clearTimeout(timer);
    }
  }, [isDropdownOpen]);

  const handleCategorySelect = (categoryId: SearchCategory) => {
    setDraftCategory(categoryId);
    setIsDropdownOpen(false);
  };

  const runSearch = () => {
    const category = submitSearch();
    if (window.location.pathname === '/') {
      window.dispatchEvent(
        new CustomEvent('worldasia:search-submitted', { detail: { category } })
      );
      scrollToSection('shop-products');
      return;
    }

    const params = new URLSearchParams();
    params.set('scroll', 'shop-products');
    if (draftQuery.trim()) {
      params.set('q', draftQuery.trim());
    }
    if (category !== 'all') {
      params.set('category', category);
    }
    navigate(`/?${params.toString()}`);
  };

  const handleSearchSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    runSearch();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const goHomeSection = (sectionId: string) => {
    requestHomeSection(sectionId, navigate);
  };

  const goComingSoon = (feature: string) => {
    navigate(`/coming-soon?feature=${encodeURIComponent(feature)}`);
  };

  const handleCustomerSupport = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
      return;
    }
    if (window.location.pathname === '/') {
      window.dispatchEvent(new CustomEvent('worldasia:open-customer-support'));
      return;
    }
    navigate('/?support=1');
  };

  const handleLocationClick = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
    } else {
      // Open Account Settings with address tab
      setSettingsInitialTab('addresses');
      setIsSettingsOpen(true);
    }
  };

  const handleCartClick = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
    } else {
      openCart();
    }
  };

  const handleProfileClick = () => {
    if (!isAuthenticated) {
      openAuthModal('login');
    } else {
      setSettingsInitialTab('profile');
      setIsSettingsOpen(true);
    }
  };

  return (
    <nav className={`navigation ${isScrolled ? 'navigation-scrolled' : ''}`}>
      {/* Promotional Banner - Hidden on Mobile */}
      <div className={`promo-banner ${isScrolled ? 'promo-banner-hidden' : ''} desktop-only`}>
        <div className="promo-container">
          <div className="promo-left">
            <span className="promo-text">Your Gateway to Asian Shopping</span>
          </div>
          <div className="promo-center">
            <span className="promo-subtext">Connect with authentic Asian vendors for products and services worldwide.</span>
          </div>
          <div className="promo-right">
            <button className="order-now-btn" onClick={() => openAuthModal('signup')}>Join Now</button>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="main-header">
        <div className="header-container">
          {/* Mobile Header - Simplified */}
          <div className="mobile-header">
            <div className="logo">
              <img src="https://i.imghippo.com/files/YbW7011PQ.png" alt="Worldasia Logo" className="logo-image" />
            </div>
            <div className="mobile-actions">
                              <div className="cart-icon" onClick={handleCartClick}>
                  <div className="cart-icon-container">
                    <ShoppingBagIcon className="cart-bag" />
                    {cartCount > 0 && (
                      <div className="cart-count-badge-new">
                        <span>{cartCount}</span>
                      </div>
                    )}
                  </div>
                </div>
              <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Desktop Header */}
          <div className="desktop-header">
            <div className="header-left">
              <div className="logo">
                <img src="https://i.imghippo.com/files/YbW7011PQ.png" alt="Worldasia Logo" className="logo-image" />
              </div>
              <div className="delivery-location" onClick={handleLocationClick}>
                <svg className="location-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                <span className="location-text">Location: Set your address</span>
              </div>
            </div>
            
            <div className="search-section">
              <form className="search-bar" onSubmit={handleSearchSubmit}>
                <div className="category-dropdown">
                  <button
                    type="button"
                    className="category-selector"
                    onClick={toggleDropdown}
                    aria-expanded={isDropdownOpen}
                  >
                    <span>{selectedCategoryLabel}</span>
                    <svg className="dropdown-arrow" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 10l5 5 5-5z"/>
                    </svg>
                  </button>
                  {isDropdownVisible && (
                    <div className={`dropdown-menu ${isDropdownAnimating ? 'dropdown-animating' : 'dropdown-exiting'}`}>
                      {SEARCH_CATEGORY_OPTIONS.map((option, index) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`dropdown-item ${draftCategory === option.id ? 'active' : ''}`}
                          onClick={() => handleCategorySelect(option.id)}
                          style={{ '--item-index': index } as React.CSSProperties}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button type="submit" className="search-submit-btn" aria-label="Search">
                  <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  </svg>
                </button>
                <input
                  type="search"
                  value={draftQuery}
                  onChange={(event) => setDraftQuery(event.target.value)}
                  placeholder={
                    draftCategory === 'all'
                      ? 'Search Worldasia...'
                      : `Search ${selectedCategoryLabel.toLowerCase()}...`
                  }
                  className="search-input"
                />
              </form>
            </div>

            <div className="header-right">
              <div className="user-actions">
                <div className="cart-icon" onClick={handleCartClick}>
                  <div className="cart-icon-container">
                    <ShoppingBagIcon className="cart-bag" />
                    {cartCount > 0 && (
                      <div className="cart-count-badge-new">
                        <span>{cartCount}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="profile-icon" onClick={handleProfileClick}>
                  <svg className="profile-avatar" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'mobile-menu-open' : ''}`}>
        <div className="mobile-menu-content">
          <div className="mobile-menu-header">
            <span className="mobile-menu-title">Menu</span>
            <button className="mobile-menu-close" onClick={toggleMobileMenu}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
          
          {/* Mobile Search */}
          <div className="mobile-search">
            <form className="mobile-search-bar" onSubmit={handleSearchSubmit}>
              <button type="submit" className="mobile-search-submit" aria-label="Search">
                <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
              </button>
              <input
                type="search"
                value={draftQuery}
                onChange={(event) => setDraftQuery(event.target.value)}
                placeholder="Search Worldasia..."
                className="mobile-search-input"
              />
            </form>
          </div>

          <div className="mobile-menu-items">
            <a href="#shop-products" className="mobile-menu-item" onClick={(e) => { e.preventDefault(); toggleMobileMenu(); goHomeSection('shop-products'); }}>
              <Squares2X2Icon className="mobile-menu-icon" width={20} height={20} />
              Products
            </a>
            <a href="#shop-products" className="mobile-menu-item" onClick={(e) => { e.preventDefault(); toggleMobileMenu(); goHomeSection('shop-products'); }}>
              <svg className="mobile-menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z"/>
              </svg>
              Deals & Offers
            </a>
            <div className="mobile-menu-divider"></div>
            <a href="#profile" className="mobile-menu-item" onClick={(e) => { e.preventDefault(); handleProfileClick(); }}>
              <svg className="mobile-menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              My Account
            </a>
            <a href="#support" className="mobile-menu-item" onClick={(e) => { e.preventDefault(); toggleMobileMenu(); handleCustomerSupport(); }}>
              <svg className="mobile-menu-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
              </svg>
              Help & Support
            </a>
          </div>
        </div>
      </div>

      {/* Account Settings Modal */}
      <AccountSettings 
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        initialTab={settingsInitialTab}
      />

      {/* Main Navigation Menu - Desktop Only */}
      <div className={`main-menu ${isScrolled ? 'main-menu-hidden' : ''} desktop-only`}>
        <div className="menu-container">
          <div className="menu-left">
            <a href="#shop-products" className="menu-link" onClick={(e) => { e.preventDefault(); goHomeSection('shop-products'); }}>Products</a>
            <a href="#become-vendor" className="menu-link" onClick={(e) => { e.preventDefault(); openAuthModal('vendor-signup'); }}>Become A Vendor</a>
          </div>
          <div className="menu-right">
            <a href="/coming-soon?feature=faqs" className="utility-link" onClick={(e) => { e.preventDefault(); goComingSoon('faqs'); }}>FAQs</a>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation; 